import React, { useState, useContext, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    ToggleButtonGroup,
    ToggleButton,
    Chip,
    Stack,
    Badge,
    useTheme,
    alpha,
    Fade,
    Collapse,
    Button,
    IconButton,
    Autocomplete,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import {
    Person as PersonIcon,
    Groups as GroupsIcon,
    PersonOff as PersonOffIcon,
    ExpandMore as ExpandMoreIcon,
    FilterAlt as FilterAltIcon,
    Clear as ClearIcon,
    CheckBox as CheckBoxIcon,
    TableChart as TableChartIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { User, WorkerQueryFilters, WorkerAvailabilityStatus, QuestionFilter } from '../../models/worker';
import UserAvatar from '../UserAvatar';
import { useSelector } from 'react-redux';
import type { RootState } from '../../redux/store';
import { SettingsContext } from '../../context/SettingsContext';
import ReactCountryFlag from 'react-country-flag';
import DynamicPersonalFilters from './DynamicPersonalFilters';
import DynamicProfessionFilters from './DynamicProfessionFilters';
import { WorkerLifecycleFilterFields } from './WorkerLifecycleFilterFields';
import { useWorkerLifecycleFilterControls } from './useWorkerLifecycleFilterControls';
import { LANGUAGE_TO_COUNTRY, NATIONALITY_LABELS, NATIONALITY_OPTIONS } from './workerFilterOptions';

type WorkerCounts = {
    [userId: string]: number;
} & {
    unassigned?: number;
    total?: number;
};

export interface ClientFilter {
    type: 'all' | 'my-clients' | 'unassigned' | 'specific-user';
    userId?: number;
    role?: 'ACCOUNTMANAGER' | 'RECRUITER';
}

interface UnifiedWorkerFilterProps {
    users: User[];
    onFilterChange: (clientFilter: ClientFilter, advancedFilters: WorkerQueryFilters) => void;
    workerCounts?: WorkerCounts;
    initialAdvancedFilters?: WorkerQueryFilters;
    onToggleBulkSelect?: () => void;
    onExport?: () => void;
    bulkSelectMode?: boolean;
    selectedCount?: number;
}

const UnifiedWorkerFilter: React.FC<UnifiedWorkerFilterProps> = ({
    users,
    onFilterChange,
    workerCounts = {},
    initialAdvancedFilters = {},
    onToggleBulkSelect,
    onExport,
    bulkSelectMode = false,
    selectedCount = 0,
}) => {
    const theme = useTheme();
    const currentUserAuth = useSelector((state: RootState) => state.auth);
    const { settings } = useContext(SettingsContext);
    
    // Main expand/collapse state
    const [expanded, setExpanded] = useState(false);
    
    // Client filter state
    const [selectedFilter, setSelectedFilter] = useState<ClientFilter>({ type: 'all' });
    const [expandedUsers, setExpandedUsers] = useState(true);
    
    // Advanced filter states
    const [workerAvailability, setWorkerAvailability] = useState<string>(
        initialAdvancedFilters.workerAvailability || ""
    );
    const [proposedCertNames, setCertificationNames] = useState<string[]>(
        initialAdvancedFilters.proposedCertNames || []
    );
    const [languages, setLanguages] = useState<string[]>(
        initialAdvancedFilters.languages || []
    );
    const [nationalities, setNationalities] = useState<string[]>(
        initialAdvancedFilters.nationalities || []
    );
    const [professions, setProfessions] = useState<string[]>(
        initialAdvancedFilters.professions || []
    );
    const lifecycle = useWorkerLifecycleFilterControls(initialAdvancedFilters);
    // Dynamic filters
    const [personalQuestionFilters, setPersonalQuestionFilters] = useState<QuestionFilter[]>(initialAdvancedFilters.questionFilters || []);
    const [professionQuestionFilters, setProfessionQuestionFilters] = useState<QuestionFilter[]>([]);
    
    // Separate users by role
    const accountManagers = users.filter(u => u.role === 'ACCOUNTMANAGER');
    const recruiters = users.filter(u => u.role === 'RECRUITER');
    
    // Find current user details
    const currentUserDetails = users.find(u => 
        u.email === currentUserAuth?.user?.email || u.username === currentUserAuth?.user?.username
    );
    
    // Get available options from settings
    const availableLanguages = Object.keys(LANGUAGE_TO_COUNTRY);
    const availableProfessions = settings?.professions || [];
    const [availableCertNames, setAvailableCertNames] = useState<string[]>([]);


    useEffect(() => {
        import('../../api/workerCertificationsApi').then(({ fetchCertNameSuggestions }) =>
            fetchCertNameSuggestions()
                .then(rows => setAvailableCertNames(rows.map(r => r.proposedCertName)))
                .catch(() => setAvailableCertNames([])),
        );
    }, []);
    
    // Status color mapping
    const handleClientFilterChange = (newFilter: ClientFilter) => {
        const advancedFilters = getAdvancedFilters();
        lifecycle.apply(() => {
            setSelectedFilter(newFilter);
            applyFilters(newFilter, advancedFilters);
        });
    };
    
    const handleQuickFilter = (_: React.MouseEvent<HTMLElement>, value: string | null) => {
        if (!value) return;
        
        let newFilter: ClientFilter;
        switch (value) {
            case 'all':
                newFilter = { type: 'all' };
                break;
            case 'my-clients':
                if (currentUserDetails) {
                    newFilter = { 
                        type: 'my-clients', 
                        userId: currentUserDetails.id,
                        role: currentUserDetails.role as 'ACCOUNTMANAGER' | 'RECRUITER'
                    };
                } else {
                    return;
                }
                break;
            case 'unassigned':
                newFilter = { type: 'unassigned' };
                break;
            default:
                return;
        }
        handleClientFilterChange(newFilter);
    };
    
    const handleUserSelect = (user: User) => {
        // Only allow filtering for account managers and recruiters
        if (user.role === 'ACCOUNTMANAGER' || user.role === 'RECRUITER') {
            handleClientFilterChange({
                type: 'specific-user',
                userId: user.id,
                role: user.role as 'ACCOUNTMANAGER' | 'RECRUITER'
            });
        }
    };
    
    const getUserWorkerCount = (userId: number): number => {
        if (!workerCounts || typeof workerCounts !== 'object') return 0;
        return workerCounts[String(userId)] || 0;
    };
    
    const getAdvancedFilters = (): WorkerQueryFilters => {
        const filters: WorkerQueryFilters = { ...lifecycle.value };

        if (workerAvailability) filters.workerAvailability = workerAvailability as WorkerAvailabilityStatus;
        if (proposedCertNames.length > 0) filters.proposedCertNames = proposedCertNames;
        if (languages.length > 0) filters.languages = languages;
        if (nationalities.length > 0) filters.nationalities = nationalities;
        if (professions.length > 0) filters.professions = professions;
        const combinedQF = [...personalQuestionFilters, ...professionQuestionFilters];
        if (combinedQF.length > 0) filters.questionFilters = combinedQF;
        
        return filters;
    };
    
    const applyFilters = (clientFilter: ClientFilter, advancedFilters: WorkerQueryFilters) => {
        onFilterChange(clientFilter, advancedFilters);
    };
    
    const handleApplyAdvancedFilters = () => {
        const advancedFilters = getAdvancedFilters();
        lifecycle.apply(() => {
            applyFilters(selectedFilter, advancedFilters);
            // Collapse only after a valid request has fired.
            setExpanded(false);
        });
    };
    
    const handleClearAdvancedFilters = () => {
        setWorkerAvailability("");
        setCertificationNames([]);
        setLanguages([]);
        setNationalities([]);
        setProfessions([]);
        setPersonalQuestionFilters([]);
        setProfessionQuestionFilters([]);
        lifecycle.clear();
        applyFilters(selectedFilter, {});
    };

    // Clear EVERYTHING (client filter + advanced) and re-apply — used by the
    // "Clear" shortcut in the collapsed header so filters can be reset without
    // opening the panel.
    const handleClearAll = () => {
        setSelectedFilter({ type: 'all' });
        setWorkerAvailability("");
        setCertificationNames([]);
        setLanguages([]);
        setNationalities([]);
        setProfessions([]);
        setPersonalQuestionFilters([]);
        setProfessionQuestionFilters([]);
        lifecycle.clear();
        applyFilters({ type: 'all' }, {});
    };

    const getAdvancedFilterCount = () => {
        let count = 0;
        if (workerAvailability) count++;
        if (proposedCertNames.length > 0) count++;
        if (languages.length > 0) count++;
        if (nationalities.length > 0) count++;
        if (professions.length > 0) count++;
        count += lifecycle.activeCount;
        return count;
    };
    
    const getFilterSummary = () => {
        const parts: string[] = [];
        
        // Client filter
        if (selectedFilter.type === 'my-clients') {
            parts.push('My Clients');
        } else if (selectedFilter.type === 'unassigned') {
            parts.push('Unassigned');
        } else if (selectedFilter.type === 'specific-user' && selectedFilter.userId) {
            const user = users.find(u => u.id === selectedFilter.userId);
            if (user) parts.push(`${user.firstName}'s Clients`);
        }
        
        // Other filters
        if (professions.length > 0) {
            parts.push(`${professions.length} profession${professions.length > 1 ? 's' : ''}`);
        }
        if (languages.length > 0) {
            parts.push(`${languages.length} language${languages.length > 1 ? 's' : ''}`);
        }
        if (proposedCertNames.length > 0) {
            parts.push(`${proposedCertNames.length} cert${proposedCertNames.length > 1 ? 's' : ''}`);
        }
        if (nationalities.length > 0) {
            parts.push(`${nationalities.length} nationalit${nationalities.length > 1 ? 'ies' : 'y'}`);
        }
        if (workerAvailability) {
            parts.push(workerAvailability.replace(/_/g, ' ').toLowerCase());
        }
        parts.push(...lifecycle.summary);

        return parts.length > 0 ? parts.join(', ') : 'No filters applied';
    };
    
    return (
        <Fade in timeout={600}>
            <Paper
                elevation={0}
                sx={{
                    mb: 2,
                    background: theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.dark, 0.1)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    borderRadius: 2,
                }}
            >
                {/* Collapsed Header Bar */}
                <Box 
                    sx={{ 
                        p: 2, 
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        }
                    }}
                    onClick={() => setExpanded(!expanded)}
                >
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                            <FilterAltIcon color="primary" />
                            <Typography variant="h6" fontWeight="600">
                                Filters & Actions
                            </Typography>
                            
                            {/* Filter Summary */}
                            <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                    fontStyle: (selectedFilter.type === 'all' && getAdvancedFilterCount() === 0) ? 'italic' : 'normal'
                                }}
                            >
                                {getFilterSummary()}
                            </Typography>
                            
                            {/* Active filter badges + one-click Clear (no need to open the panel) */}
                            {(selectedFilter.type !== 'all' || getAdvancedFilterCount() > 0) && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Badge badgeContent={getAdvancedFilterCount() + (selectedFilter.type !== 'all' ? 1 : 0)} color="primary">
                                        <Chip
                                            label="Active"
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </Badge>
                                    <Button
                                        size="small"
                                        variant="text"
                                        color="inherit"
                                        startIcon={<ClearIcon fontSize="small" />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleClearAll();
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </Stack>
                            )}
                        </Stack>
                        
                        {/* Action Buttons - Always Visible */}
                        <Stack direction="row" spacing={1} alignItems="center">
                            {bulkSelectMode ? (
                                <>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleBulkSelect?.();
                                        }}
                                        color="inherit"
                                    >
                                        Cancel Selection
                                    </Button>
                                    {selectedCount > 0 && (
                                        <Chip 
                                            label={`${selectedCount} selected`}
                                            size="small"
                                            color="info"
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<CheckBoxIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleBulkSelect?.();
                                        }}
                                        size="small"
                                    >
                                        Bulk Select
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        startIcon={<TableChartIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onExport?.();
                                        }}
                                        size="small"
                                        sx={{
                                            borderColor: 'success.main',
                                            color: 'success.main',
                                            '&:hover': {
                                                borderColor: 'success.dark',
                                                backgroundColor: alpha(theme.palette.success.main, 0.08),
                                            }
                                        }}
                                    >
                                        Export
                                    </Button>
                                </>
                            )}
                            
                            <IconButton 
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setExpanded(!expanded);
                                }}
                            >
                                {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        </Stack>
                    </Stack>
                </Box>
                
                {/* Expanded Content */}
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                            {/* Left Panel - Quick Filters */}
                            <Box sx={{ 
                                flex: { xs: '1', md: '0 0 350px' },
                                borderRight: { md: 1 },
                                borderColor: 'divider',
                                p: 2
                            }}>
                        {/* Quick Filter Buttons */}
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Quick Filters
                        </Typography>
                        <ToggleButtonGroup
                            value={selectedFilter.type}
                            exclusive
                            onChange={handleQuickFilter}
                            size="small"
                            fullWidth
                            sx={{
                                mb: 2,
                                '& .MuiToggleButton-root': {
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                }
                            }}
                        >
                            <ToggleButton value="all">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <GroupsIcon sx={{ fontSize: 16 }} />
                                    <span>All</span>
                                    <Chip 
                                        label={workerCounts.total || 0} 
                                        size="small"
                                        sx={{ height: 16, fontSize: '0.7rem' }}
                                    />
                                </Stack>
                            </ToggleButton>
                            
                            {currentUserDetails && (currentUserDetails.role === 'ACCOUNTMANAGER' || currentUserDetails.role === 'RECRUITER') && (
                                <ToggleButton value="my-clients">
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                        <PersonIcon sx={{ fontSize: 16 }} />
                                        <span>Mine</span>
                                        <Chip 
                                            label={getUserWorkerCount(currentUserDetails.id)} 
                                            size="small"
                                            color="primary"
                                            sx={{ height: 16, fontSize: '0.7rem' }}
                                        />
                                    </Stack>
                                </ToggleButton>
                            )}
                            
                            <ToggleButton value="unassigned">
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <PersonOffIcon sx={{ fontSize: 16 }} />
                                    <span>Unassigned</span>
                                    <Chip 
                                        label={workerCounts?.unassigned || 0} 
                                        size="small"
                                        color="warning"
                                        sx={{ height: 16, fontSize: '0.7rem' }}
                                    />
                                </Stack>
                            </ToggleButton>
                        </ToggleButtonGroup>
                        
                        {/* User List */}
                        <Box>
                            <Button
                                size="small"
                                fullWidth
                                variant={expandedUsers ? "contained" : "outlined"}
                                onClick={() => setExpandedUsers(!expandedUsers)}
                                endIcon={<ExpandMoreIcon sx={{
                                    transform: expandedUsers ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s',
                                }} />}
                                sx={{ mb: 1 }}
                            >
                                Filter by User ({accountManagers.length + recruiters.length})
                            </Button>
                            
                            <Collapse in={expandedUsers}>
                                <Box sx={{ 
                                    maxHeight: 300,
                                    overflowY: 'auto',
                                    pr: 1,
                                }}>
                                    {accountManagers.length > 0 && (
                                        <>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                Account Managers
                                            </Typography>
                                            {accountManagers.map(user => (
                                                <Paper
                                                    key={user.id}
                                                    elevation={selectedFilter.userId === user.id ? 2 : 0}
                                                    sx={{
                                                        p: 1,
                                                        mb: 1,
                                                        cursor: 'pointer',
                                                        border: `1px solid ${
                                                            selectedFilter.userId === user.id 
                                                                ? theme.palette.primary.main 
                                                                : 'transparent'
                                                        }`,
                                                        backgroundColor: selectedFilter.userId === user.id
                                                            ? alpha(theme.palette.primary.main, 0.08)
                                                            : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                        }
                                                    }}
                                                    onClick={() => handleUserSelect(user)}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Badge
                                                            badgeContent={getUserWorkerCount(user.id)}
                                                            color="primary"
                                                            max={99}
                                                        >
                                                            <UserAvatar user={user} size={32} />
                                                        </Badge>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" noWrap>
                                                                {user.firstName} {user.lastName}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </>
                                    )}
                                    
                                    {recruiters.length > 0 && (
                                        <>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, mt: 2 }}>
                                                Recruiters
                                            </Typography>
                                            {recruiters.map(user => (
                                                <Paper
                                                    key={user.id}
                                                    elevation={selectedFilter.userId === user.id ? 2 : 0}
                                                    sx={{
                                                        p: 1,
                                                        mb: 1,
                                                        cursor: 'pointer',
                                                        border: `1px solid ${
                                                            selectedFilter.userId === user.id 
                                                                ? theme.palette.primary.main 
                                                                : 'transparent'
                                                        }`,
                                                        backgroundColor: selectedFilter.userId === user.id
                                                            ? alpha(theme.palette.primary.main, 0.08)
                                                            : 'transparent',
                                                        '&:hover': {
                                                            backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                                        }
                                                    }}
                                                    onClick={() => handleUserSelect(user)}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Badge
                                                            badgeContent={getUserWorkerCount(user.id)}
                                                            color="primary"
                                                            max={99}
                                                        >
                                                            <UserAvatar user={user} size={32} />
                                                        </Badge>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography variant="body2" noWrap>
                                                                {user.firstName} {user.lastName}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            ))}
                                        </>
                                    )}
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>
                    
                    {/* Right Panel - Advanced Filters */}
                    <Box sx={{ flex: 1, p: 2 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                Advanced Filters
                            </Typography>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={handleClearAdvancedFilters}
                                    disabled={getAdvancedFilterCount() === 0}
                                    startIcon={<ClearIcon />}
                                >
                                    Clear
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={handleApplyAdvancedFilters}
                                    disabled={getAdvancedFilterCount() === 0 || !lifecycle.valid}
                                >
                                    Apply
                                </Button>
                            </Stack>
                        </Stack>
                        
                        <Stack spacing={2}>
                            {/* Personal filters (dynamic) */}
                            <DynamicPersonalFilters language={settings?.defaultLanguage || 'EN'} value={personalQuestionFilters} onChange={setPersonalQuestionFilters} />
                            {/* Profession filters (dynamic) */}
                            <DynamicProfessionFilters language={settings?.defaultLanguage || 'EN'} professionKeys={professions} value={professionQuestionFilters} onChange={setProfessionQuestionFilters} />
                            <WorkerLifecycleFilterFields value={lifecycle.value} onChange={lifecycle.setValue} />
                            {/* Availability (status itself is owned by the top tabs) */}
                            <FormControl size="small" fullWidth>
                                <InputLabel>Availability Status</InputLabel>
                                <Select
                                    value={workerAvailability}
                                    onChange={(e) => setWorkerAvailability(e.target.value)}
                                    label="Availability Status"
                                >
                                    <MenuItem value="">None</MenuItem>
                                    {Object.values(WorkerAvailabilityStatus).map(status => (
                                        <MenuItem key={status} value={status}>
                                            {status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            {/* Skills and Qualifications */}
                            <Autocomplete
                                multiple
                                size="small"
                                options={availableProfessions.map(p => p.professionKey)}
                                value={professions}
                                onChange={(_, newValue) => setProfessions(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Professions" placeholder="Select professions" />
                                )}
                            />
                            
                            <Autocomplete
                                multiple
                                size="small"
                                options={availableCertNames}
                                value={proposedCertNames}
                                onChange={(_, newValue) => setCertificationNames(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Certifications" placeholder="Select certifications" />
                                )}
                            />
                            
                            {/* Languages and Nationalities */}
                            <Autocomplete
                                multiple
                                size="small"
                                options={availableLanguages}
                                value={languages}
                                onChange={(_, newValue) => setLanguages(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Languages" placeholder="Select languages" />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <ReactCountryFlag
                                            countryCode={LANGUAGE_TO_COUNTRY[option] || 'UN'}
                                            svg
                                            style={{ width: '1.5em', height: '1.5em', marginRight: 8 }}
                                        />
                                        {option}
                                    </Box>
                                )}
                            />
                            
                            <Autocomplete
                                multiple
                                size="small"
                                options={NATIONALITY_OPTIONS.map((o) => o.code)}
                                getOptionLabel={(code) => NATIONALITY_LABELS[code] ?? code}
                                value={nationalities}
                                onChange={(_, newValue) => setNationalities(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Nationalities" placeholder="Select nationalities" />
                                )}
                            />
                        </Stack>
                    </Box>
                </Box>
                    </Box>
                </Collapse>
            </Paper>
        </Fade>
    );
};

export default UnifiedWorkerFilter;
